import { PROFESSION_CODES } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/constants';
import { get, isEmpty, isNil } from 'lodash';
import {
  CHOICE,
  INJURY_POST_DECEASED,
} from 'containers/PreOpeningPage/constants';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { rcCoporeal } from 'containers/CourtManagerLegalProceedings/constants';

import {
  addPriorityReasonProfession,
  removeRejectedVictims,
  removeWhiteSpaceFromString,
} from 'services/utils';
import { resolveSagisClaim } from 'helpers/utils';
import { VICTIM } from 'containers/data-structure';
import { MANAGEMENT_MODES } from 'containers/RecapManagerPage/constants';
import { formatOpposingParties } from 'containers/CourtManagerLegalProceedings/utils';
import { VictimType } from 'typings/globalTypes';
import { ClaimRecapType } from 'typings/claimTypes';

export const formatBodyClaimToUpdate = (
  dataClaimToUpdate: any,
  claimRecap: ClaimRecapType,
  priorityReasonClaimId: any,
  listings: any,
  policyInfo: any,
  riskId: any,
  claimId: number,
  claimNumber: string,
) => {
  const updatePriorityVictim =
    !removeRejectedVictims(get(claimRecap, 'victims', [])).find((v: any) =>
      PROFESSION_CODES.includes(v.professionCode),
    ) && priorityReasonClaimId === 5;
  const claimResponsabilityRate =
    policyInfo &&
    get(resolveSagisClaim(policyInfo), 'offeredRisks', []).find(
      (element: { riskName: string }) => element.riskName === rcCoporeal,
    )?.responsibilityRate;

  const formattedSagisVictims = [...get(dataClaimToUpdate, 'victims', [])]
    .filter((v: any) => !v.isDeleted && !v.id)
    .map((victim: VictimType) => {
      let setDefaultBirthDate = false;
      if (isEmpty(victim[VICTIM.DATE_OF_BIRTH])) {
        setDefaultBirthDate = true;
      }
      if (victim.injuryPostKey === INJURY_POST_DECEASED) {
        victim.nextOfKins = victim.beneficiaries.map((el: any) => {
          return {
            fullName: `${removeWhiteSpaceFromString(
              el.firstName,
            )} ${removeWhiteSpaceFromString(el.lastName)}`,
            qualityCode: Number(el.beneficiaryType),
            age: el.age,
            birthDate: el?.dateOfBirth,
            funeralFees: featureService.hasFeature(FEATURES.NEW_LAW_DAHIR)
              ? el.funeralFees
              : !isNil(el.funeralFees) && !!Number(claimResponsabilityRate)
              ? el.funeralFees / (Number(claimResponsabilityRate) / 100)
              : el.funeralFees,
            handicapIndicator: el.handicapIndicator ? 'Y' : 'N',
            dependentIndicator: el.dependentIndicator ? 'Y' : 'N',
          };
        });
      }
      return {
        ...victim,
        id: undefined,
        victimFirstName: victim[VICTIM.FIRST_NAME],
        victimLastName: victim[VICTIM.LAST_NAME],
        victimBirthDate: victim[VICTIM.DATE_OF_BIRTH],
        injuryCode:
          String(victim[VICTIM.INJURY_POST_KEY]) === INJURY_POST_DECEASED
            ? victim[VICTIM.INJURY_POST_KEY]
            : 0,
        qualityVictim: victim[VICTIM.QUALITY_KEY],
        classVictim: victim[VICTIM.CLASS_KEY],
        Town: victim[VICTIM.CITY],
        isExcluded: false,
        Complement: victim[VICTIM.ADDRESS],
        isWorkAccident: victim[VICTIM.WORK_ACCIDENT_ID] === 1 ? true : false,
        setDefaultBirthDate,
        cspCode: get(
          get(listings, 'Profession', []).find(
            (el: any) => el.ItemKey === victim.professionCode,
          ),
          'ItemGroup',
          null,
        ),
        gender: !isNil(victim[VICTIM.GENDER]) ? victim[VICTIM.GENDER] : '',
        itt: parseInt((victim[VICTIM.ITT] as unknown) as string),
      };
    });

  const formattedSagisVictimsToUpdate = [
    ...get(dataClaimToUpdate, 'victims', []),
  ]
    .filter((v: any) => v.isModified && v.id)
    .map((victim: VictimType) => {
      let setDefaultBirthDate = false;
      if (isEmpty(victim[VICTIM.DATE_OF_BIRTH])) {
        setDefaultBirthDate = true;
      }
      return {
        ...victim,
        victimFirstName: victim[VICTIM.FIRST_NAME],
        victimLastName: victim[VICTIM.LAST_NAME],
        victimBirthDate: victim[VICTIM.DATE_OF_BIRTH],
        injuryCode:
          String(victim[VICTIM.INJURY_POST_KEY]) === INJURY_POST_DECEASED
            ? victim[VICTIM.INJURY_POST_KEY]
            : 0,
        qualityVictim: victim[VICTIM.QUALITY_KEY],
        classVictim: victim[VICTIM.CLASS_KEY],
        Town: victim[VICTIM.CITY],
        isExcluded: false,
        gender: !isNil(victim[VICTIM.GENDER]) ? victim[VICTIM.GENDER] : '',
        Complement: victim[VICTIM.ADDRESS],
        isWorkAccident: victim[VICTIM.WORK_ACCIDENT_ID] === 1,
        setDefaultBirthDate,
        cspCode: get(
          get(listings, 'Profession', []).find(
            (el: any) => el.ItemKey === victim.professionCode,
          ),
          'ItemGroup',
          null,
        ),
        itt: parseFloat((victim[VICTIM.ITT] as unknown) as string),
      };
    });
  const beneficiariesToDelete = [...get(dataClaimToUpdate, 'victims', [])]
    .filter((v: any) => v.injuryPostKey === INJURY_POST_DECEASED)
    .map((victim: VictimType) => {
      let beneficiaries = get(victim, 'beneficiariesToDelete', []).map(
        (el: any) => el.nextOfKinKey,
      );
      return beneficiaries;
    });
  const formattedSagisVehiculesToUpdate = get(
    dataClaimToUpdate,
    'vehicules',
    [],
  )
    .filter((v: any) => v.isModified && v.id)
    .map((vehicule: any) => {
      return {
        ...vehicule,
        insuranceCompanyCode: vehicule?.opposingParty,
        partyKey: vehicule?.partyKey,
        riskID: riskId,
      };
    });
  return {
    ...dataClaimToUpdate,
    claim: {
      ...get(dataClaimToUpdate, 'claim'),
      priorityReasonId: addPriorityReasonProfession(
        priorityReasonClaimId,
        !!removeRejectedVictims(get(claimRecap, 'victims', [])).find((v: any) =>
          PROFESSION_CODES.includes(v.professionCode),
        ),
        listings,
      )
        ? addPriorityReasonProfession(
            priorityReasonClaimId,
            !!removeRejectedVictims(
              get(claimRecap, 'victims', []),
            ).find((v: any) => PROFESSION_CODES.includes(v.professionCode)),
            listings,
          )
        : updatePriorityVictim && priorityReasonClaimId != 4
        ? null
        : undefined,
    },
    driver: !isEmpty(get(dataClaimToUpdate, 'driver'))
      ? {
          ...get(dataClaimToUpdate, 'driver'),
          isDriverTheInsured:
            get(dataClaimToUpdate, 'driver.isDriverTheInsured') === CHOICE.NO
              ? false
              : Boolean(get(dataClaimToUpdate, 'driver.isDriverTheInsured')),
        }
      : {},
    isLawyer: false,
    victimsSagisToAdd: [...formattedSagisVictims],
    victimsSagisToUpdate: [...formattedSagisVictimsToUpdate],
    isTrackingActive: featureService.hasFeature(FEATURES.TRACKING_ACTIONS_USER),
    claimCircumstances: {
      scaleCaseCode: get(dataClaimToUpdate, 'claim.scaleCaseKey', {}),
      circumstanceCode: get(
        dataClaimToUpdate,
        'claim.accidentCirconstanceKey',
        {},
      ),
      responsabilityRate: get(dataClaimToUpdate, 'claim.responsabilityKey', {}),
      managementMode: MANAGEMENT_MODES.COMMON_RIGHT,
      riskId,
      isFromCorporel: true,
    },
    beneficiariesToDelete,
    opposingPartiesSagisToAdd: formatOpposingParties(
      get(dataClaimToUpdate, 'vehicules', []).filter(
        (v: any) => !v.isDeleted && !v.id,
      ),
      riskId,
      claimNumber,
      claimId as any,
      get(listings, 'compagnies'),
    ),
    opposingPartiesSagisToUpdate: formattedSagisVehiculesToUpdate,
    riskKey: riskId,
    claimNumber: get(dataClaimToUpdate, 'claim.claimNumber'),
  };
};
